/**
 * LegalComplianceHubModal
 * OPO hub for the Legal & Compliance department (Dept 02).
 * Tabs: Command Center | Laws & Assessments | Ministry of Experts | Touchpoint Audit | Settings
 */
import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import './LegalComplianceHubModal.css';

const API = '/api/opo/legal';

const TABS = [
  { id: 'command',     label: 'Command Center' },
  { id: 'laws',        label: 'Laws & Assessments' },
  { id: 'ministry',    label: 'Ministry of Experts' },
  { id: 'touchpoints', label: 'Touchpoint Audit' },
  { id: 'settings',    label: 'Settings' },
];

const STATUS_COLORS = {
  tabled: '#f59e0b', in_committee: '#f59e0b', passed: '#3b82f6',
  in_force: '#10b981', in_effect: '#10b981', repealed: '#6b7280',
  ok: '#10b981', stale: '#f59e0b', missing: '#ef4444', link_out: '#6b7280',
};

const statusDot = (s) => (
  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[s] || '#6b7280', marginRight: 6, flexShrink: 0 }} />
);

const IMPACT_LABELS = {
  tenants: 'Tenants',
  brokers: 'Brokers / Agents',
  companies: 'Companies (incl. Optimum Realty Inc.)',
  ai_companies: 'AI-specific companies (incl. OptiPrime AI Inc.)',
};

function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-CA') : '—'; }
function fmtDateTime(s) { return s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—'; }

// ── Command Center ────────────────────────────────────────────────────────────
function CommandCenter({ overview, onOpenLaw, onResolveAlert }) {
  if (!overview) return <div className="lc-loading">Loading overview…</div>;

  const laws = overview.laws || [];
  const alerts = overview.unresolved_alerts || [];
  const tpCounts = {};
  (overview.touchpoint_counts || []).forEach(r => { tpCounts[r.status] = r.count; });
  const assessed = laws.filter(l => l.latest_version != null).length;
  const settings = overview.settings || {};

  return (
    <div>
      <div className="lc-stat-grid">
        <div className="lc-stat-card">
          <div className="lc-stat-label">Tracked Laws</div>
          <div className="lc-stat-value">{laws.length}</div>
        </div>
        <div className="lc-stat-card">
          <div className="lc-stat-label">Assessed by Ministry</div>
          <div className="lc-stat-value lc-stat-blue">{assessed}</div>
        </div>
        <div className="lc-stat-card">
          <div className="lc-stat-label">Unresolved Alerts</div>
          <div className={`lc-stat-value ${alerts.length ? 'lc-stat-red' : 'lc-stat-green'}`}>{alerts.length}</div>
        </div>
        <div className="lc-stat-card">
          <div className="lc-stat-label">Touchpoints OK</div>
          <div className="lc-stat-value lc-stat-green">{tpCounts['ok'] || 0}</div>
        </div>
        <div className="lc-stat-card">
          <div className="lc-stat-label">Missing Policies</div>
          <div className={`lc-stat-value ${tpCounts['missing'] ? 'lc-stat-amber' : 'lc-stat-green'}`}>{tpCounts['missing'] || 0}</div>
        </div>
        <div className="lc-stat-card">
          <div className="lc-stat-label">Daily Budget</div>
          <div className="lc-stat-value">{settings.daily_budget_minutes || 60}<span style={{ fontSize: 13, fontWeight: 400 }}> min</span></div>
        </div>
      </div>

      <div className="lc-two-col">
        <div className="lc-panel">
          <h3 className="lc-panel-title">Active Alerts</h3>
          {alerts.length === 0
            ? <p className="lc-muted">No active alerts.</p>
            : alerts.slice(0, 8).map(a => (
              <div key={a.id} className="lc-row">
                <span className={`lc-badge lc-badge-${a.severity}`}>{a.severity}</span>
                <span className="lc-row-title">{a.title || (a.summary_md || '').slice(0, 80)}</span>
                <span className="lc-muted lc-row-date">{fmtDate(a.detected_at)}</span>
                <button type="button" className="lc-btn-outline" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => onResolveAlert(a.id)}>Resolve</button>
              </div>
            ))
          }
        </div>

        <div className="lc-panel">
          <h3 className="lc-panel-title">Latest Published Assessments</h3>
          {(overview.latest_assessments || []).length === 0
            ? <p className="lc-muted">No assessments published yet. Convene the Ministry of Experts from the Ministry tab.</p>
            : (overview.latest_assessments || []).map(a => (
              <div key={`${a.law_id}-${a.version}`} className="lc-row" style={{ cursor: 'pointer' }} onClick={() => onOpenLaw(a.law_code)}>
                <span className={`lc-badge ${a.consensus_reached ? 'lc-badge-green' : 'lc-badge-amber'}`}>{a.consensus_reached ? 'consensus' : 'partial'}</span>
                <span className="lc-row-title">{a.law_name} <span className="lc-muted">v{a.version}</span></span>
                <span className="lc-muted lc-row-date">{fmtDate(a.published_at)}</span>
              </div>
            ))
          }
        </div>
      </div>

      <div className="lc-panel">
        <h3 className="lc-panel-title">Tracked Laws</h3>
        {laws.map(l => (
          <div key={l.code} className="lc-row" style={{ cursor: 'pointer' }} onClick={() => onOpenLaw(l.code)}>
            {statusDot(l.status)}
            <span className="lc-row-title">{l.name}</span>
            <span className="lc-badge lc-badge-accent">{l.instrument_type}</span>
            <span className="lc-badge lc-badge-muted">{l.status.replace(/_/g, ' ')}</span>
            <span className="lc-muted lc-row-date">{l.latest_version ? `assessment v${l.latest_version}` : 'not yet assessed'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Assessment renderer ───────────────────────────────────────────────────────
function AssessmentView({ assessment }) {
  if (!assessment) return <p className="lc-muted">No published assessment yet for this law. The Ministry of Experts will publish one on its next run.</p>;
  const impact = assessment.impact_md || {};
  const citations = assessment.citations || [];
  return (
    <div>
      <div className="lc-card-meta" style={{ marginBottom: 12 }}>
        <span className={`lc-badge ${assessment.consensus_reached ? 'lc-badge-green' : 'lc-badge-amber'}`}>
          {assessment.consensus_reached ? 'Consensus reached' : 'Partial consensus'}
        </span>
        <span className="lc-muted">v{assessment.version} · published {fmtDateTime(assessment.published_at)} · {assessment.debate_rounds || '—'} debate round(s) · {assessment.model || ''}</span>
      </div>

      <div className="lc-panel">
        <h3 className="lc-panel-title">What this law means (plain language)</h3>
        <div className="lc-md"><ReactMarkdown>{assessment.layman_summary_md || ''}</ReactMarkdown></div>
      </div>

      <div className="lc-panel">
        <h3 className="lc-panel-title">Impact on participants</h3>
        {Object.entries(IMPACT_LABELS).map(([key, label]) => (
          impact[key] ? (
            <div key={key} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text, #e8eaf6)', marginBottom: 6 }}>{label}</div>
              <div className="lc-md"><ReactMarkdown>{impact[key]}</ReactMarkdown></div>
            </div>
          ) : null
        ))}
      </div>

      {citations.length > 0 && (
        <div className="lc-panel">
          <h3 className="lc-panel-title">Citations (live links)</h3>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>
            {citations.map((c, i) => (
              <li key={i}><a href={c.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent, #19efff)' }}>{c.label || c.url}</a></li>
            ))}
          </ul>
        </div>
      )}

      {assessment.consensus_notes && (
        <div className="lc-panel">
          <h3 className="lc-panel-title">Consensus notes</h3>
          <div className="lc-md"><ReactMarkdown>{assessment.consensus_notes}</ReactMarkdown></div>
        </div>
      )}
    </div>
  );
}

// ── Law detail (with assessment version history) ─────────────────────────────
function LawDetail({ code, onBack }) {
  const [data, setData] = useState(null);
  const [assessments, setAssessments] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    fetch(`${API}/laws/${code}`).then(r => r.json()).then(setData).catch(() => {});
    fetch(`${API}/laws/${code}/assessments`).then(r => r.json()).then(d => setAssessments(d.assessments || [])).catch(() => setAssessments([]));
  }, [code]);

  if (!data) return <div className="lc-loading">Loading law…</div>;
  const { law } = data;
  const current = selectedVersion != null
    ? (assessments || []).find(a => a.version === selectedVersion)
    : data.latest_assessment;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text, #e8eaf6)', margin: '0 0 6px' }}>{law.name}</h2>
        <div className="lc-card-meta">
          {statusDot(law.status)}
          <span className="lc-badge lc-badge-accent">{law.instrument_type}</span>
          <span className="lc-badge lc-badge-muted">{law.status.replace(/_/g, ' ')}</span>
          <span className="lc-muted">{law.jurisdiction}</span>
          {law.tabled_date && <span className="lc-muted">tabled {fmtDate(law.tabled_date)}</span>}
          {law.regulator && <span className="lc-muted">regulator: {law.regulator}</span>}
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {(law.official_urls || []).map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer" style={{ color: 'var(--accent, #19efff)', fontSize: 12 }}>Official source {i + 1} ↗</a>
          ))}
          {(assessments || []).length > 1 && (
            <select className="lc-select" style={{ width: 'auto' }} value={selectedVersion ?? (data.latest_assessment?.version || '')} onChange={e => setSelectedVersion(parseInt(e.target.value, 10))}>
              {(assessments || []).map(a => (
                <option key={a.version} value={a.version}>Assessment v{a.version} — {fmtDate(a.published_at)}</option>
              ))}
            </select>
          )}
          {data.latest_source_md && (
            <button type="button" className="lc-btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setShowSource(s => !s)}>
              {showSource ? 'Hide archived source' : 'Read archived source'}
            </button>
          )}
        </div>
      </div>

      {law.notes && <p className="lc-muted" style={{ marginBottom: 16 }}>{law.notes}</p>}

      {showSource && data.latest_source_md && (
        <div className="lc-panel">
          <h3 className="lc-panel-title">Archived source ({(data.source_files || [])[0] || ''})</h3>
          <div className="lc-md" style={{ maxHeight: 360, overflowY: 'auto' }}><ReactMarkdown>{data.latest_source_md}</ReactMarkdown></div>
        </div>
      )}

      <AssessmentView assessment={current} />

      <button type="button" className="lc-back-btn" onClick={onBack}>← Back to laws</button>
    </div>
  );
}

// ── Laws list ─────────────────────────────────────────────────────────────────
function LawsList({ onSelect }) {
  const [laws, setLaws] = useState(null);

  useEffect(() => {
    fetch(`${API}/laws`).then(r => r.json()).then(d => setLaws(d.laws || [])).catch(() => setLaws([]));
  }, []);

  if (!laws) return <div className="lc-loading">Loading laws…</div>;

  return (
    <div className="lc-card-grid">
      {laws.map(l => (
        <div key={l.code} className="lc-card" style={{ cursor: 'pointer' }} onClick={() => onSelect(l.code)}>
          <div className="lc-card-header">
            {statusDot(l.status)}
            <span className="lc-card-name">{l.name}</span>
          </div>
          <div className="lc-card-meta">
            <span className="lc-badge lc-badge-accent">{l.instrument_type}</span>
            <span className="lc-badge lc-badge-muted">{l.status.replace(/_/g, ' ')}</span>
            <span className="lc-muted">{l.jurisdiction}</span>
          </div>
          <div className="lc-card-meta">
            <span className={`lc-badge ${l.latest_version ? 'lc-badge-green' : 'lc-badge-amber'}`}>
              {l.latest_version ? `assessment v${l.latest_version}` : 'awaiting first assessment'}
            </span>
            {l.last_checked_at && <span className="lc-muted">checked {fmtDate(l.last_checked_at)}</span>}
          </div>
          {l.notes && <div className="lc-card-notes">{l.notes.slice(0, 150)}{l.notes.length > 150 ? '…' : ''}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Ministry of Experts tab ───────────────────────────────────────────────────
function MinistryTab({ overview }) {
  const [agents, setAgents] = useState(null);
  const [runMsg, setRunMsg] = useState(null);
  const [error, setError] = useState(null);
  const [transcriptLaw, setTranscriptLaw] = useState('');
  const [transcript, setTranscript] = useState(null);

  const loadAgents = useCallback(() => {
    fetch(`${API}/agents`).then(r => r.json()).then(d => setAgents(d.agents || [])).catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    loadAgents();
    const t = setInterval(loadAgents, 20000);
    return () => clearInterval(t);
  }, [loadAgents]);

  useEffect(() => {
    if (!transcriptLaw) { setTranscript(null); return; }
    fetch(`${API}/laws/${transcriptLaw}/transcript`).then(r => r.json()).then(setTranscript).catch(() => setTranscript(null));
  }, [transcriptLaw]);

  const runAgent = (type) => {
    setError(null);
    fetch(`${API}/agents/${type}/run`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else { setRunMsg(d.message); setTimeout(loadAgents, 1500); }
      })
      .catch(e => setError(e.message));
  };

  const AGENT_INFO = {
    ministry: { name: 'Ministry of Experts', desc: 'Expert panel reads, debates in rounds, forms consensus, publishes versioned assessments.' },
    scholar: { name: 'Scholar', desc: 'Re-fetches official law sources, detects changes, archives snapshots.' },
    touchpoint_auditor: { name: 'Touchpoint Auditor', desc: 'Hashes and versions all public terms/privacy surfaces.' },
  };

  const laws = (overview && overview.laws) || [];
  const rounds = {};
  ((transcript && transcript.transcript) || []).forEach(t => {
    rounds[t.round] = rounds[t.round] || [];
    rounds[t.round].push(t);
  });

  return (
    <div>
      {runMsg && <div className="lc-run-msg">{runMsg}</div>}
      {error && <div className="lc-error">{error}</div>}

      <div className="lc-panel">
        <h3 className="lc-panel-title">Expert Panel</h3>
        <div className="lc-card-meta">
          {(((overview && overview.settings) || {}).expert_panel || []).map(e => (
            <span key={e.key} className="lc-badge lc-badge-accent" title={e.focus}>{e.name}</span>
          ))}
          <span className="lc-muted">Roster and debate settings editable in the Settings tab.</span>
        </div>
      </div>

      <div className="lc-agent-cards" style={{ marginBottom: 16 }}>
        {(agents || []).map(a => {
          const info = AGENT_INFO[a.type] || { name: a.type, desc: '' };
          const lr = a.last_run;
          return (
            <div key={a.type} className="lc-agent-card">
              <div className="lc-agent-card-header">
                <span className="lc-agent-card-name">{info.name}</span>
                <span className={`lc-badge ${lr?.status === 'completed' ? 'lc-badge-green' : lr?.status === 'running' ? 'lc-badge-blue' : lr?.status === 'failed' ? 'lc-badge-red' : 'lc-badge-muted'}`}>
                  {lr?.status || 'never run'}
                </span>
              </div>
              <div className="lc-agent-meta">
                <span>{info.desc}</span>
                <span>Last run: {lr ? fmtDateTime(lr.started_at) : '—'}</span>
              </div>
              {lr?.summary_md && <div className="lc-agent-log">{lr.summary_md}</div>}
              {lr?.error_message && <div className="lc-error" style={{ fontSize: 11 }}>{lr.error_message}</div>}
              <button type="button" className="lc-btn" disabled={lr?.status === 'running'} onClick={() => runAgent(a.type)}>
                {lr?.status === 'running' ? 'Running…' : 'Run now'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="lc-panel">
        <h3 className="lc-panel-title">Debate Transcript</h3>
        <div style={{ marginBottom: 12 }}>
          <select className="lc-select" style={{ width: 'auto' }} value={transcriptLaw} onChange={e => setTranscriptLaw(e.target.value)}>
            <option value="">Select a law to read the latest debate…</option>
            {laws.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </div>
        {transcriptLaw && transcript && transcript.transcript && transcript.transcript.length === 0 && (
          <p className="lc-muted">No debate transcript yet for this law — run the Ministry of Experts.</p>
        )}
        {Object.entries(rounds).map(([round, entries]) => (
          <div key={round} className="lc-transcript-round">
            <div className="lc-transcript-round-title">Round {round}</div>
            {entries.map(t => (
              <div key={t.id} className="lc-transcript-entry">
                <div className="lc-transcript-expert">{t.expert}</div>
                <div className="lc-md"><ReactMarkdown>{t.position_md}</ReactMarkdown></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Touchpoint Audit tab ──────────────────────────────────────────────────────
function TouchpointsTab() {
  const [touchpoints, setTouchpoints] = useState(null);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffFrom, setDiffFrom] = useState('');
  const [diffTo, setDiffTo] = useState('');

  useEffect(() => {
    fetch(`${API}/touchpoints`).then(r => r.json()).then(d => setTouchpoints(d.touchpoints || [])).catch(() => setTouchpoints([]));
  }, []);

  useEffect(() => {
    if (!selected) { setVersions(null); setDiff(null); setDiffFrom(''); setDiffTo(''); return; }
    fetch(`${API}/touchpoints/${selected.id}/versions`).then(r => r.json()).then(d => {
      const v = d.versions || [];
      setVersions(v);
      if (v.length >= 2) { setDiffFrom(String(v[1].id)); setDiffTo(String(v[0].id)); }
    }).catch(() => setVersions([]));
  }, [selected]);

  const loadDiff = () => {
    if (!selected || !diffFrom || !diffTo) return;
    fetch(`${API}/touchpoints/${selected.id}/diff?from=${diffFrom}&to=${diffTo}`)
      .then(r => r.json()).then(setDiff).catch(() => setDiff(null));
  };

  if (!touchpoints) return <div className="lc-loading">Loading touchpoints…</div>;

  if (selected) {
    return (
      <div>
        <button type="button" className="lc-back-btn" onClick={() => setSelected(null)}>← All touchpoints</button>
        <h3 style={{ color: 'var(--text, #e8eaf6)', margin: '0 0 6px' }}>{selected.site} — {selected.policy_type}</h3>
        <div className="lc-card-meta" style={{ marginBottom: 14 }}>
          {statusDot(selected.status)}
          <span className="lc-badge lc-badge-muted">{selected.status}</span>
          {selected.live_url && <a href={selected.live_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent, #19efff)', fontSize: 12 }}>Live page ↗</a>}
          {selected.source_path && <span className="lc-mono">{selected.source_path}</span>}
        </div>
        {selected.notes && <p className="lc-muted" style={{ marginBottom: 14 }}>{selected.notes}</p>}

        <div className="lc-panel">
          <h3 className="lc-panel-title">Version history (append-only audit)</h3>
          {(versions || []).length === 0
            ? <p className="lc-muted">No snapshots yet — run the Touchpoint Auditor from the Ministry tab.</p>
            : (
              <table className="lc-table">
                <thead>
                  <tr><th>Captured</th><th>SHA-256</th><th>Size</th><th>Change</th><th>By</th></tr>
                </thead>
                <tbody>
                  {(versions || []).map(v => (
                    <tr key={v.id}>
                      <td>{fmtDateTime(v.captured_at)}</td>
                      <td className="lc-mono">{(v.content_sha256 || '').slice(0, 16)}…</td>
                      <td>{v.content_bytes != null ? `${Math.round(v.content_bytes / 1024)} KB` : '—'}</td>
                      <td>{v.change_summary || '—'}</td>
                      <td>{v.changed_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>

        {(versions || []).length >= 2 && (
          <div className="lc-panel">
            <h3 className="lc-panel-title">Compare versions</h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <select className="lc-select" style={{ width: 'auto' }} value={diffFrom} onChange={e => setDiffFrom(e.target.value)}>
                {(versions || []).map(v => <option key={v.id} value={v.id}>From: {fmtDateTime(v.captured_at)}</option>)}
              </select>
              <select className="lc-select" style={{ width: 'auto' }} value={diffTo} onChange={e => setDiffTo(e.target.value)}>
                {(versions || []).map(v => <option key={v.id} value={v.id}>To: {fmtDateTime(v.captured_at)}</option>)}
              </select>
              <button type="button" className="lc-btn" onClick={loadDiff}>Diff</button>
            </div>
            {diff && (
              <div className="lc-two-col">
                <div>
                  <div className="lc-muted" style={{ marginBottom: 6 }}>Removed lines ({diff.removed_count})</div>
                  <div className="lc-diff-col">{(diff.removed_lines || []).map((l, i) => <div key={i} className="lc-diff-removed">− {l}</div>)}</div>
                </div>
                <div>
                  <div className="lc-muted" style={{ marginBottom: 6 }}>Added lines ({diff.added_count})</div>
                  <div className="lc-diff-col">{(diff.added_lines || []).map((l, i) => <div key={i} className="lc-diff-added">+ {l}</div>)}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="lc-muted" style={{ marginBottom: 14 }}>
        Every public terms / privacy / consent surface across OptiPrime AI Inc. and Optimum Realty Inc.
        The Touchpoint Auditor hashes each surface daily and records a new version whenever content changes — so you can always audit what changed, when.
      </p>
      <table className="lc-table">
        <thead>
          <tr><th>Site</th><th>Policy</th><th>Status</th><th>Versions</th><th>Last snapshot</th><th>Source</th></tr>
        </thead>
        <tbody>
          {touchpoints.map(t => (
            <tr key={t.id} className="lc-clickable" onClick={() => setSelected(t)}>
              <td>{t.site}</td>
              <td>{t.policy_type.replace(/_/g, ' ')}</td>
              <td>{statusDot(t.status)}<span className={`lc-badge ${t.status === 'ok' ? 'lc-badge-green' : t.status === 'missing' ? 'lc-badge-red' : t.status === 'stale' ? 'lc-badge-amber' : 'lc-badge-muted'}`}>{t.status}</span></td>
              <td>{t.version_count || 0}</td>
              <td>{t.latest_version_at ? fmtDateTime(t.latest_version_at) : '—'}</td>
              <td className="lc-mono">{t.source_path || t.live_url || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────
function SettingsTab({ onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/settings`).then(r => r.json()).then(d => setSettings(d.settings)).catch(() => {});
  }, []);

  if (!settings) return <div className="lc-loading">Loading settings…</div>;

  const panel = Array.isArray(settings.expert_panel) ? settings.expert_panel : [];

  const update = (patch) => setSettings(s => ({ ...s, ...patch }));
  const updateExpert = (i, patch) => {
    const next = panel.map((e, j) => (j === i ? { ...e, ...patch } : e));
    update({ expert_panel: next });
  };

  const save = () => {
    setSaving(true); setSaved(false); setError(null);
    fetch(`${API}/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        daily_budget_minutes: settings.daily_budget_minutes,
        schedule_enabled: settings.schedule_enabled,
        model: settings.model,
        max_debate_rounds: settings.max_debate_rounds,
        expert_panel: panel,
      }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else { setSaved(true); onSaved?.(); setTimeout(() => setSaved(false), 3000); }
      })
      .catch(e => setError(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      {error && <div className="lc-error">{error}</div>}
      <div className="lc-panel">
        <h3 className="lc-panel-title">Daily Session</h3>
        <div className="lc-settings-grid">
          <div className="lc-field">
            <label>Time budget (minutes / day)</label>
            <input className="lc-input" type="number" min="5" max="720" value={settings.daily_budget_minutes}
              onChange={e => update({ daily_budget_minutes: parseInt(e.target.value, 10) || 60 })} />
          </div>
          <div className="lc-field">
            <label>Max debate rounds per law</label>
            <input className="lc-input" type="number" min="1" max="10" value={settings.max_debate_rounds}
              onChange={e => update({ max_debate_rounds: parseInt(e.target.value, 10) || 3 })} />
          </div>
          <div className="lc-field">
            <label>Model (local Ollama)</label>
            <input className="lc-input" type="text" value={settings.model} onChange={e => update({ model: e.target.value })} />
          </div>
          <div className="lc-field">
            <label>Daily schedule</label>
            <div className="lc-checkbox-row">
              <input type="checkbox" checked={!!settings.schedule_enabled} onChange={e => update({ schedule_enabled: e.target.checked })} />
              <span>Run automatically every day (PM2 <code style={{ fontSize: 11 }}>dev-legal-ministry</code>)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="lc-panel">
        <h3 className="lc-panel-title">Expert Panel Roster</h3>
        <p className="lc-muted" style={{ marginBottom: 12 }}>Each expert is an AI persona in the daily debate. Edit names and specialist focus areas.</p>
        {panel.map((e, i) => (
          <div key={e.key || i} className="lc-expert-row">
            <input className="lc-input" value={e.name} onChange={ev => updateExpert(i, { name: ev.target.value })} />
            <input className="lc-input" value={e.focus} onChange={ev => updateExpert(i, { focus: ev.target.value })} />
          </div>
        ))}
      </div>

      <button type="button" className="lc-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
      {saved && <span className="lc-save-note">Saved ✓</span>}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function LegalComplianceHubModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('command');
  const [overview, setOverview] = useState(null);
  const [selectedLaw, setSelectedLaw] = useState(null);

  const fetchOverview = useCallback(() => {
    fetch(`${API}/overview`).then(r => r.json()).then(setOverview).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) fetchOverview();
    const interval = setInterval(fetchOverview, 60000);
    return () => clearInterval(interval);
  }, [isOpen, fetchOverview]);

  if (!isOpen) return null;

  const openLaw = (code) => { setSelectedLaw(code); setActiveTab('laws'); };
  const resolveAlert = (id) => {
    fetch(`${API}/alerts/${id}/resolve`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(() => fetchOverview()).catch(() => {});
  };

  return (
    <div className="lc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="lc-modal" onClick={e => e.stopPropagation()}>
        <div className="lc-header">
          <div className="lc-header-left">
            <span className="lc-header-icon">⚖️</span>
            <h2 className="lc-header-title">Legal &amp; Compliance</h2>
            <span className="lc-header-dept">Department 02 — OptiPrime AI Inc.</span>
          </div>
          <button type="button" className="lc-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="lc-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              className={`lc-tab ${activeTab === t.id ? 'lc-tab-active' : ''}`}
              onClick={() => { setActiveTab(t.id); setSelectedLaw(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="lc-content">
          {activeTab === 'command' && (
            <CommandCenter overview={overview} onOpenLaw={openLaw} onResolveAlert={resolveAlert} />
          )}

          {activeTab === 'laws' && !selectedLaw && <LawsList onSelect={setSelectedLaw} />}
          {activeTab === 'laws' && selectedLaw && <LawDetail code={selectedLaw} onBack={() => setSelectedLaw(null)} />}

          {activeTab === 'ministry' && <MinistryTab overview={overview} />}

          {activeTab === 'touchpoints' && <TouchpointsTab />}

          {activeTab === 'settings' && <SettingsTab onSaved={fetchOverview} />}
        </div>
      </div>
    </div>
  );
}
